import { useCallback, useEffect, useRef, useState } from 'react';

import StaticAdsService from 'services/StaticAdsService';
import { shouldPoll } from 'shared/pollingGate';

import { isStaticAdBusy } from './staticStatus';
import { StaticPipelineRow, StaticPipelineStatus } from './types';

const POLL_INTERVAL_MS = 8000;

function isUnfinished(status: StaticPipelineStatus) {
  return isStaticAdBusy(status);
}

/**
 * Track pipeline status for whatever is currently on screen.
 *
 * One poller for the whole grid, hitting one narrow endpoint — not one per card.
 * It stops entirely once nothing is queued or running — an ad resting at NEW is
 * not polled, because nothing is going to change it until someone asks. Every tick is gated on
 * shouldPoll() so a dashboard left open in a background tab doesn't keep
 * Snowflake resumed (the cost incident that gate was written for).
 */
export default function useStaticPipeline(
  service: StaticAdsService,
  staticAdIds: string[],
  onSettled?: () => void,
) {
  const [statuses, setStatuses] = useState<Record<string, StaticPipelineRow>>({});
  // Kept in a ref so changing the callback doesn't restart the interval.
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  const idsKey = staticAdIds.join(',');

  const poll = useCallback(async (ids: string[]) => {
    const resp = await service.getPipelineStatus(ids);
    // A null response is "unknown", not "settled": keep the interval alive so
    // a transient API failure can't freeze every badge at its last state and
    // fire onSettled over work still running. pollingGate bounds the cost of
    // polling through a persistent outage.
    if (!resp) return true;
    setStatuses((prev) => {
      const next = { ...prev };
      resp.statuses.forEach((row) => { next[row.staticAdId] = row; });
      return next;
    });
    return resp.statuses.some(row => isUnfinished(row.pipelineStatus));
  }, [service]);

  useEffect(() => {
    const ids = idsKey ? idsKey.split(',') : [];
    if (!ids.length) return undefined;

    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const stop = () => {
      if (timer) { clearInterval(timer); timer = null; }
    };

    const tick = async () => {
      if (cancelled) return;
      if (!shouldPoll()) return; // cheap no-op while hidden/idle; resumes on return
      const stillRunning = await poll(ids);
      if (cancelled) return;
      if (!stillRunning) {
        stop();
        onSettledRef.current?.();
      }
    };

    // One immediate read so a freshly-loaded grid shows real status rather than
    // waiting a full interval, then settle into polling.
    void tick();
    timer = setInterval(tick, POLL_INTERVAL_MS);

    return () => { cancelled = true; stop(); };
  }, [idsKey, poll]);

  return { statuses };
}
